import {
  Facebook,
  Instagram,
  LinkedIn,
  LoginFooterMail,
  Phone,
  Xicon,
  Youtube,
} from "@/assets";
import { socialLinks } from "@/constants/apiConstantData/socialLinks/script";
import { useLogin } from "@/context/useLoginProps";
import { getSiteProps } from "@/utils";
import React, { Fragment, useMemo } from "react";

import { FooterLink } from "@/components/common/Footer/types";

import { loginRightFooterProps } from "../../types";

const LoginRightFooter: React.FC<loginRightFooterProps> = (props) => {
  const { recruiterLogin, socialLink, contactInfo, mail } = props;
  const { isMobile, hostname } = useLogin();
  const siteConfig = useMemo(() => getSiteProps(hostname), [hostname]);
  const channel = siteConfig?.site_map || "rexmonster";

  const getSocialLink = (name: string) => {
    const social = socialLink.find(
      (item: FooterLink) => item.name.toLowerCase() === name.toLowerCase(),
    );
    return social?.link_rewrite || "";
  };

  return (
    <div className="flex w-full flex-wrap items-start justify-evenly py-[42px] max-md:flex-col max-md:!items-center max-md:gap-8 max-md:px-8 max-md:text-center">
      {contactInfo && contactInfo.length > 0 ? (
        <>
          <div className="flex flex-col">
            <h2 className="text-sm font-bold leading-none text-gray-600">
              Contact Us
            </h2>
            <div
              className={`flex ${isMobile ? "flex-row gap-[9px]" : "flex-col"}`}
            >
              <div
                className={`mt-2 flex ${isMobile ? "w-1/2" : "w-full"} gap-2 ${!isMobile && contactInfo.length > 1 ? "items-start" : "items-center"}`}
              >
                <div className="flex h-8 min-h-8 w-8 items-center justify-center gap-3.5 self-stretch overflow-hidden rounded-[56.471px] border-[0.667px] border-solid border-[color:var(--Border-Subtle,#E8E8EB)] bg-white px-1.5">
                  <Phone height={16} width={16} />
                </div>
                <div className="flex w-full max-w-[240px] flex-wrap items-center gap-2">
                  {contactInfo.map((contact, index) => {
                    const linkRewrite = contact?.link_rewrite ?? "";
                    const hasNext = index < contactInfo.length - 1;
                    return (
                      <Fragment key={index}>
                        <a
                          href={linkRewrite}
                          className="my-auto self-stretch text-wrap text-left text-xs leading-none text-black"
                        >
                          {contact.name}
                        </a>
                        {hasNext && !isMobile ? (
                          <div className="my-auto flex h-[13px] w-px shrink-0 self-stretch bg-gray-200" />
                        ) : null}
                      </Fragment>
                    );
                  })}
                </div>
              </div>

              <div
                className={`mt-2 flex h-max ${isMobile ? "w-1/2" : "w-full"} items-center gap-2`}
              >
                <div className="flex h-8 min-h-8 w-8 items-center justify-center gap-3.5 self-stretch overflow-hidden rounded-[56.471px] border-[0.667px] border-solid border-[color:var(--Border-Subtle,#E8E8EB)] bg-white px-1.5">
                  <LoginFooterMail height={16} width={16} />
                </div>
                <a
                  href={mail[0].link_rewrite ?? ""}
                  aria-label="mail at"
                  title="mail at"
                  className="flex items-center self-stretch text-xs leading-none text-black hover:underline"
                >
                  {mail[0].name}
                </a>
              </div>
            </div>
          </div>

          {!isMobile && (
            <div className="flex h-[103px] w-px shrink-0 rounded-[40px] bg-gray-200" />
          )}
        </>
      ) : null}

      {socialLink && socialLink.length > 0 ? (
        <>
          <div className="flex flex-col gap-[11px]">
            <div className="text-sm font-bold leading-none text-gray-600">
              Follow us
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {[
                {
                  Icon: Instagram,
                  alt: "Instagram",
                  link: getSocialLink("Instagram"),
                  isVisible:
                    socialLinks[channel as keyof typeof socialLinks]?.instagram,
                },
                {
                  Icon: LinkedIn,
                  alt: "LinkedIn",
                  link: getSocialLink("Linkedin"),
                  isVisible:
                    socialLinks[channel as keyof typeof socialLinks]?.linkedin,
                },
                {
                  Icon: Xicon,
                  alt: "Twitter",
                  link: getSocialLink("Twitter"),
                  isVisible:
                    socialLinks[channel as keyof typeof socialLinks]?.twitter,
                },
                {
                  Icon: Facebook,
                  alt: "Facebook",
                  link: getSocialLink("Facebook"),
                  isVisible:
                    socialLinks[channel as keyof typeof socialLinks]?.facebook,
                },
                {
                  Icon: Youtube,
                  alt: "Youtube",
                  link: getSocialLink("Youtube"),
                  isVisible:
                    socialLinks[channel as keyof typeof socialLinks]?.youtube,
                },
              ].map(
                ({ Icon, alt, link, isVisible }, index) =>
                  link &&
                  isVisible && (
                    <a
                      key={index}
                      href={link}
                      aria-label={alt}
                      title={alt}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="my-auto flex h-9 min-h-9 w-9 items-center justify-center gap-4 self-stretch overflow-hidden rounded-[63.529px] border-[0.75px] border-solid border-[color:var(--Border-Subtle,#E8E8EB)] bg-white px-1.5"
                    >
                      <Icon height={18} width={18} />
                    </a>
                  ),
              )}
            </div>
          </div>
          {!isMobile && (
            <div className="flex h-[103px] w-px shrink-0 rounded-[40px] bg-gray-200" />
          )}
        </>
      ) : null}

      <div className="flex flex-col gap-[11px]">
        <h2 className="text-sm font-bold leading-none text-gray-600">
          Employers Login
        </h2>
        <a
          href={`https://${recruiterLogin}/?login=yes`}
          target="_blank"
          className={`hover:bg-surface-tertiary w-full gap-1 rounded-3xl px-2 py-3 text-center text-xs text-gray-900 ${
            isMobile ? "bg-surface-tertiary" : "bg-surface-secondary"
          }`}
          rel="noopener noreferrer"
        >
          Login
        </a>
      </div>
    </div>
  );
};

export default LoginRightFooter;
